import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../../lib/api'

type PaymentMethod = 'cash' | 'card' | 'transfer'
type ManualPaymentType = 'prepayment' | 'topup' | 'refund'

interface ManagerRow { id: string; name: string; phone: string; isActive: boolean }
interface PointRow { id: string; name: string }
interface BikeRow { id: string; code: string; model: string | null; pointId: string | null; status: string }
interface ShiftLite { id: string; openedAt: string; closedAt: string | null; status: 'open' | 'closed'; manager: { id: string; name: string }; point: { id: string; name: string }; source?: 'auto' | 'manual' }

interface PaymentDraft {
  uid: string
  amount: string
  type: ManualPaymentType
  paymentMethod: PaymentMethod
  createdAtLocal: string
  pointId: string
  note: string
}

function apiError(err: unknown): string {
  const e = err as { response?: { data?: { error?: { message?: string; details?: unknown } } } }
  return e?.response?.data?.error?.message ?? 'Не удалось создать аренду'
}

function fmtShift(s: ShiftLite): string {
  const opened = new Date(s.openedAt).toLocaleString('ru', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
  const closed = s.closedAt ? new Date(s.closedAt).toLocaleString('ru', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : 'открыта'
  return `${s.point.name} · ${s.manager.name} · ${opened} → ${closed}${s.source === 'manual' ? ' 📝' : ''}`
}

export default function ManualRentalPage() {
  const navigate = useNavigate()
  const qc = useQueryClient()

  // Customer
  const [customerPhone, setCustomerPhone] = useState('')
  const [customerName, setCustomerName] = useState('')

  // Manager + shifts + points
  const [pickupPointId, setPickupPointId] = useState('')
  const [returnPointId, setReturnPointId] = useState('')
  const [managerId, setManagerId] = useState('')
  const [openShiftId, setOpenShiftId] = useState('')
  const [closeShiftId, setCloseShiftId] = useState('')

  // Times
  const [actualStartLocal, setActualStartLocal] = useState('')
  const [actualReturnLocal, setActualReturnLocal] = useState('')
  const [plannedReturnLocal, setPlannedReturnLocal] = useState('')
  const [completed, setCompleted] = useState(false)

  // Bikes (multi)
  const [bikeIds, setBikeIds] = useState<string[]>([])

  // Pricing
  const [pricingMode, setPricingMode] = useState<'hourly' | 'daily'>('hourly')
  const [priceOverride, setPriceOverride] = useState('')
  const [showPriceOverride, setShowPriceOverride] = useState(false)
  const [prepaymentAmount, setPrepaymentAmount] = useState('0')

  // Payments
  const [payments, setPayments] = useState<PaymentDraft[]>([])

  // Comment
  const [manualNote, setManualNote] = useState('')

  const [submitError, setSubmitError] = useState<string | null>(null)

  const { data: managersData } = useQuery<{ managers: ManagerRow[] }>({
    queryKey: ['managers'],
    queryFn: () => api.get('/users').then((r) => r.data),
  })
  const { data: pointsData } = useQuery<{ points: PointRow[] }>({
    queryKey: ['points'],
    queryFn: () => api.get('/points').then((r) => r.data),
  })
  const { data: bikesData } = useQuery<{ bikes: BikeRow[] }>({
    queryKey: ['bikes'],
    queryFn: () => api.get('/bikes').then((r) => r.data),
  })
  const { data: shiftsData } = useQuery<{ shifts: ShiftLite[] }>({
    queryKey: ['shifts-for-manual', pickupPointId],
    queryFn: () => api.get('/shifts', { params: {
      from: new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString(),
      to: new Date().toISOString(),
      limit: 200,
    } }).then((r) => r.data),
  })

  const managers = (managersData?.managers ?? []).filter((m) => m.isActive)
  const points = pointsData?.points ?? []
  const bikes = bikesData?.bikes ?? []
  const allShifts = shiftsData?.shifts ?? []

  // Shifts at pickup point covering actualStartAt
  const actualStartDate = actualStartLocal ? new Date(actualStartLocal) : null
  const actualReturnDate = actualReturnLocal ? new Date(actualReturnLocal) : null

  const matchingOpenShifts = useMemo(() => {
    if (!pickupPointId || !actualStartDate || isNaN(actualStartDate.getTime())) return []
    return allShifts.filter((s) =>
      s.point.id === pickupPointId &&
      new Date(s.openedAt) <= actualStartDate &&
      (s.closedAt == null || new Date(s.closedAt) >= actualStartDate),
    )
  }, [allShifts, pickupPointId, actualStartDate])

  const closePoint = returnPointId || pickupPointId
  const matchingCloseShifts = useMemo(() => {
    if (!completed || !closePoint || !actualReturnDate || isNaN(actualReturnDate.getTime())) return []
    return allShifts.filter((s) =>
      s.point.id === closePoint &&
      new Date(s.openedAt) <= actualReturnDate &&
      (s.closedAt == null || new Date(s.closedAt) >= actualReturnDate),
    )
  }, [allShifts, completed, closePoint, actualReturnDate])

  // Auto-fill manager from chosen open shift
  useEffect(() => {
    if (!openShiftId) return
    const s = allShifts.find((x) => x.id === openShiftId)
    if (s) setManagerId(s.manager.id)
  }, [openShiftId, allShifts])

  // Auto-pick first matching shift if only one
  useEffect(() => {
    if (matchingOpenShifts.length === 1 && !openShiftId) {
      setOpenShiftId(matchingOpenShifts[0]!.id)
    }
  }, [matchingOpenShifts, openShiftId])

  useEffect(() => {
    if (matchingCloseShifts.length === 1 && !closeShiftId) {
      setCloseShiftId(matchingCloseShifts[0]!.id)
    }
  }, [matchingCloseShifts, closeShiftId])

  // Cascade preview — pickup + return points
  const startIso = actualStartDate && !isNaN(actualStartDate.getTime()) ? actualStartDate.toISOString() : null
  const { data: cascadePickup } = useQuery<{ count: number; limit: number }>({
    queryKey: ['cascade-preview', pickupPointId, startIso],
    queryFn: () => api.get('/shifts/cascade-preview', { params: { pointId: pickupPointId, from: startIso } }).then((r) => r.data),
    enabled: Boolean(pickupPointId && startIso),
  })
  const { data: cascadeReturn } = useQuery<{ count: number; limit: number }>({
    queryKey: ['cascade-preview', returnPointId, startIso],
    queryFn: () => api.get('/shifts/cascade-preview', { params: { pointId: returnPointId, from: startIso } }).then((r) => r.data),
    enabled: Boolean(returnPointId && returnPointId !== pickupPointId && startIso),
  })
  const totalCascade = (cascadePickup?.count ?? 0) + (cascadeReturn?.count ?? 0)
  const cascadeLimit = cascadePickup?.limit ?? 100
  const cascadeOver = (cascadePickup?.count ?? 0) > cascadeLimit || (cascadeReturn?.count ?? 0) > cascadeLimit

  // Bike pickers
  const addBike = (id: string) => {
    if (!id || bikeIds.includes(id)) return
    setBikeIds([...bikeIds, id])
  }
  const removeBike = (id: string) => setBikeIds(bikeIds.filter((b) => b !== id))

  // Payment helpers
  const addPayment = () => {
    setPayments((arr) => [...arr, {
      uid: Math.random().toString(36).slice(2),
      amount: '',
      type: 'prepayment',
      paymentMethod: 'cash',
      createdAtLocal: actualStartLocal,
      pointId: pickupPointId,
      note: '',
    }])
  }
  const updatePayment = (uid: string, patch: Partial<PaymentDraft>) => {
    setPayments((arr) => arr.map((p) => p.uid === uid ? { ...p, ...patch } : p))
  }
  const removePayment = (uid: string) => setPayments((arr) => arr.filter((p) => p.uid !== uid))

  // Validations
  const validations: string[] = []
  if (customerPhone.trim().length < 10) validations.push('Телефон клиента — минимум 10 цифр')
  if (!pickupPointId) validations.push('Выберите точку выдачи')
  if (!actualStartLocal) validations.push('Введите время открытия')
  if (actualStartDate && actualStartDate > new Date()) validations.push('Время открытия в будущем')
  if (pricingMode === 'daily' && !plannedReturnLocal) validations.push('Для суточного режима введите планируемое время возврата')
  if (completed) {
    if (!actualReturnLocal) validations.push('Для завершённой аренды нужно фактическое время возврата')
    if (actualReturnDate && actualStartDate && actualReturnDate <= actualStartDate) validations.push('Возврат должен быть позже открытия')
    if (actualReturnDate && actualReturnDate > new Date()) validations.push('Время возврата в будущем')
    if (!closeShiftId) validations.push('Выберите смену закрытия')
  }
  if (!openShiftId) validations.push('Выберите смену открытия')
  if (!managerId) validations.push('Не определён менеджер')
  if (bikeIds.length === 0) validations.push('Добавьте хотя бы один велосипед')
  const priceOverrideNum = priceOverride.trim() ? parseFloat(priceOverride.replace(',', '.')) : null
  if (showPriceOverride && (priceOverrideNum === null || isNaN(priceOverrideNum) || priceOverrideNum < 0)) validations.push('Переопределённая стоимость ≥ 0')
  const prepayNum = parseFloat(prepaymentAmount.replace(',', '.')) || 0
  if (prepayNum < 0) validations.push('Предоплата ≥ 0')
  if (manualNote.trim().length < 3) validations.push('Комментарий минимум 3 символа')
  for (const p of payments) {
    const amt = parseFloat(p.amount.replace(',', '.'))
    if (isNaN(amt) || amt <= 0) validations.push('Платёж: сумма должна быть > 0')
    if (!p.createdAtLocal) validations.push('Платёж: укажите дату')
  }

  const create = useMutation({
    mutationFn: () => api.post('/rentals/manual', {
      customerPhone: customerPhone.trim(),
      customerName: customerName.trim() || undefined,
      managerId,
      openShiftId,
      closeShiftId: completed ? closeShiftId : null,
      pickupPointId,
      returnPointId: returnPointId || null,
      actualStartAt: actualStartDate!.toISOString(),
      plannedReturnAt: (pricingMode === 'daily' && plannedReturnLocal)
        ? new Date(plannedReturnLocal).toISOString()
        : new Date(actualStartDate!.getTime() + 24 * 60 * 60 * 1000).toISOString(),
      actualReturnAt: completed && actualReturnDate ? actualReturnDate.toISOString() : null,
      status: completed ? 'completed' : 'active',
      pricingMode,
      bikes: bikeIds.map((id) => ({ bikeId: id })),
      priceOverride: showPriceOverride && priceOverrideNum !== null ? priceOverrideNum : undefined,
      prepaymentAmount: prepayNum,
      payments: payments.map((p) => ({
        amount: parseFloat(p.amount.replace(',', '.')),
        type: p.type,
        paymentMethod: p.paymentMethod,
        createdAt: new Date(p.createdAtLocal).toISOString(),
        pointId: p.pointId || undefined,
        note: p.note.trim() || undefined,
      })),
      manualNote: manualNote.trim(),
    }).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['rentals'] })
      qc.invalidateQueries({ queryKey: ['shifts'] })
      qc.invalidateQueries({ queryKey: ['payments-list'] })
      qc.invalidateQueries({ queryKey: ['journal'] })
      navigate('/owner/rentals')
    },
    onError: (e) => setSubmitError(apiError(e)),
  })

  const handleSubmit = () => {
    setSubmitError(null)
    if (validations.length > 0) return
    create.mutate()
  }

  return (
    <div className="p-4 max-w-2xl mx-auto space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold">📝 Ручная аренда</h1>
          <p className="text-xs text-gray-500">Внесение записи из тетради задним числом</p>
        </div>
        <button onClick={() => navigate('/owner/rentals')} className="text-sm text-gray-500 hover:text-gray-700">
          ← Назад
        </button>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-xl px-3 py-2 text-xs text-amber-800">
        ⚠️ Никаких уведомлений менеджеру не уйдёт. После сохранения смены могут быть пересчитаны.
      </div>

      {/* Customer */}
      <section className="bg-white border rounded-2xl p-4 space-y-2">
        <h2 className="font-semibold text-sm">Клиент</h2>
        <div className="grid grid-cols-2 gap-2">
          <label className="text-xs text-gray-600">
            Телефон <span className="text-red-500">*</span>
            <input value={customerPhone} onChange={(e) => setCustomerPhone(e.target.value)}
              placeholder="+7 900 000 00 00"
              className="w-full mt-1 border rounded-lg px-2 py-2 text-sm" />
          </label>
          <label className="text-xs text-gray-600">
            Имя (необязательно)
            <input value={customerName} onChange={(e) => setCustomerName(e.target.value)}
              className="w-full mt-1 border rounded-lg px-2 py-2 text-sm" />
          </label>
        </div>
      </section>

      {/* Points + times */}
      <section className="bg-white border rounded-2xl p-4 space-y-2">
        <h2 className="font-semibold text-sm">Точки и время</h2>
        <div className="grid grid-cols-2 gap-2">
          <label className="text-xs text-gray-600">
            Точка выдачи <span className="text-red-500">*</span>
            <select value={pickupPointId} onChange={(e) => setPickupPointId(e.target.value)}
              className="w-full mt-1 border rounded-lg px-2 py-2 text-sm">
              <option value="">—</option>
              {points.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </label>
          <label className="text-xs text-gray-600">
            Точка возврата (если другая)
            <select value={returnPointId} onChange={(e) => setReturnPointId(e.target.value)}
              className="w-full mt-1 border rounded-lg px-2 py-2 text-sm">
              <option value="">— Совпадает с выдачей —</option>
              {points.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </label>
        </div>
        <div>
          <label className="text-xs text-gray-600">
            Открытие <span className="text-red-500">*</span>
            <input type="datetime-local" value={actualStartLocal}
              onChange={(e) => setActualStartLocal(e.target.value)}
              className="w-full mt-1 border rounded-lg px-2 py-2 text-sm" />
          </label>
        </div>

        <label className="flex items-center gap-2 text-sm mt-1">
          <input type="checkbox" checked={completed} onChange={(e) => setCompleted(e.target.checked)} />
          Аренда уже завершена
        </label>

        {completed && (
          <label className="block text-xs text-gray-600">
            Фактический возврат <span className="text-red-500">*</span>
            <input type="datetime-local" value={actualReturnLocal}
              onChange={(e) => setActualReturnLocal(e.target.value)}
              className="w-full mt-1 border rounded-lg px-2 py-2 text-sm" />
          </label>
        )}
      </section>

      {/* Shifts */}
      <section className="bg-white border rounded-2xl p-4 space-y-2">
        <h2 className="font-semibold text-sm">Смены</h2>
        <label className="block text-xs text-gray-600">
          Смена открытия <span className="text-red-500">*</span>
          <select value={openShiftId} onChange={(e) => setOpenShiftId(e.target.value)}
            className="w-full mt-1 border rounded-lg px-2 py-2 text-sm">
            <option value="">— Выберите —</option>
            {matchingOpenShifts.map((s) => <option key={s.id} value={s.id}>{fmtShift(s)}</option>)}
          </select>
          {pickupPointId && actualStartLocal && matchingOpenShifts.length === 0 && (
            <span className="block text-xs text-red-600 mt-1">
              Нет открытой смены на этой точке в момент открытия. Создайте ручную смену сначала.
            </span>
          )}
        </label>

        {completed && (
          <label className="block text-xs text-gray-600">
            Смена закрытия <span className="text-red-500">*</span>
            <select value={closeShiftId} onChange={(e) => setCloseShiftId(e.target.value)}
              className="w-full mt-1 border rounded-lg px-2 py-2 text-sm">
              <option value="">— Выберите —</option>
              {matchingCloseShifts.map((s) => <option key={s.id} value={s.id}>{fmtShift(s)}</option>)}
            </select>
            {closePoint && actualReturnLocal && matchingCloseShifts.length === 0 && (
              <span className="block text-xs text-red-600 mt-1">
                Нет открытой смены на этой точке в момент возврата.
              </span>
            )}
          </label>
        )}

        {managerId && (
          <p className="text-xs text-gray-500">Менеджер: <strong>{managers.find((m) => m.id === managerId)?.name ?? '—'}</strong></p>
        )}
      </section>

      {/* Bikes */}
      <section className="bg-white border rounded-2xl p-4 space-y-2">
        <h2 className="font-semibold text-sm">Велосипеды</h2>
        <select onChange={(e) => { addBike(e.target.value); e.target.value = '' }}
          className="w-full border rounded-lg px-2 py-2 text-sm">
          <option value="">+ Добавить велосипед</option>
          {bikes
            .filter((b) => !bikeIds.includes(b.id))
            .map((b) => <option key={b.id} value={b.id}>#{b.code} {b.model ?? ''} {b.status === 'service' ? '(сервис)' : ''}</option>)
          }
        </select>
        {bikeIds.length > 0 && (
          <div className="space-y-1">
            {bikeIds.map((id) => {
              const b = bikes.find((x) => x.id === id)
              return (
                <div key={id} className="flex items-center justify-between bg-gray-50 rounded-lg px-2 py-1 text-sm">
                  <span>#{b?.code} {b?.model ?? ''}</span>
                  <button type="button" onClick={() => removeBike(id)} className="text-red-500 text-xs">✕</button>
                </div>
              )
            })}
          </div>
        )}
      </section>

      {/* Pricing */}
      <section className="bg-white border rounded-2xl p-4 space-y-3">
        <h2 className="font-semibold text-sm">Тариф и оплата</h2>

        {/* Pricing mode */}
        <div>
          <p className="text-xs text-gray-600 mb-1.5">Режим тарификации <span className="text-red-500">*</span></p>
          <div className="grid grid-cols-2 gap-2">
            {(['hourly', 'daily'] as const).map((m) => (
              <button key={m} type="button" onClick={() => setPricingMode(m)}
                className={`py-2 rounded-xl border text-xs font-medium transition-colors ${pricingMode === m ? 'bg-brand-600 text-white border-brand-600' : 'border-gray-300 text-gray-600'}`}>
                {m === 'hourly' ? '⏱ По часам' : '🌙 По суткам'}
              </button>
            ))}
          </div>
        </div>

        {/* Planned return — only for daily */}
        {pricingMode === 'daily' && !completed && (
          <label className="block text-xs text-gray-600">
            Планируемый возврат <span className="text-red-500">*</span>
            <input type="datetime-local" value={plannedReturnLocal}
              onChange={(e) => setPlannedReturnLocal(e.target.value)}
              className="w-full mt-1 border rounded-lg px-2 py-2 text-sm" />
          </label>
        )}

        <p className="text-xs text-blue-600 bg-blue-50 border border-blue-200 rounded-lg px-2 py-1.5">
          💡 Стоимость считается автоматически по тарифу велосипеда и фактической длительности.
          {completed ? ' Цена будет вычислена при сохранении.' : ' Итоговая цена — при закрытии аренды.'}
        </p>

        {/* Optional price override */}
        {!showPriceOverride ? (
          <button type="button" onClick={() => setShowPriceOverride(true)}
            className="w-full text-xs border border-amber-300 text-amber-700 rounded-lg py-1.5 hover:bg-amber-50">
            ✏️ Переопределить стоимость вручную
          </button>
        ) : (
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <p className="text-xs font-medium text-amber-700">Ручная стоимость (переопределение)</p>
              <button type="button" onClick={() => { setShowPriceOverride(false); setPriceOverride('') }}
                className="text-xs text-gray-400">Отмена</button>
            </div>
            <input type="number" min="0" step="0.01" value={priceOverride}
              onChange={(e) => setPriceOverride(e.target.value)}
              placeholder="Сумма, ₽"
              className="w-full border border-amber-300 rounded-lg px-2 py-2 text-sm" />
          </div>
        )}

        <label className="block text-xs text-gray-600">
          Предоплата на старте, ₽
          <input type="number" min="0" step="0.01" value={prepaymentAmount}
            onChange={(e) => setPrepaymentAmount(e.target.value)}
            className="w-full mt-1 border rounded-lg px-2 py-2 text-sm" />
        </label>
      </section>

      {/* Payments */}
      <section className="bg-white border rounded-2xl p-4 space-y-2">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold text-sm">Платежи</h2>
          <button type="button" onClick={addPayment}
            className="text-xs px-2 py-1 rounded border border-brand-300 text-brand-600 hover:bg-brand-50">
            + Добавить платёж
          </button>
        </div>
        {payments.length === 0 && (
          <p className="text-xs text-gray-400">Платежей нет. Добавьте, если в тетради зафиксированы.</p>
        )}
        {payments.map((p, idx) => (
          <div key={p.uid} className="border rounded-lg p-2 space-y-1 bg-gray-50">
            <div className="flex items-center justify-between">
              <p className="text-xs font-medium">Платёж #{idx + 1}</p>
              <button type="button" onClick={() => removePayment(p.uid)} className="text-red-500 text-xs">✕</button>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <select value={p.type} onChange={(e) => updatePayment(p.uid, { type: e.target.value as ManualPaymentType })}
                className="border rounded-lg px-2 py-1.5 text-xs">
                <option value="prepayment">Предоплата</option>
                <option value="topup">Доплата</option>
                <option value="refund">Возврат</option>
              </select>
              <select value={p.paymentMethod} onChange={(e) => updatePayment(p.uid, { paymentMethod: e.target.value as PaymentMethod })}
                className="border rounded-lg px-2 py-1.5 text-xs">
                <option value="cash">💵 Нал</option>
                <option value="card">💳 Безнал</option>
                <option value="transfer">📲 Перевод</option>
              </select>
              <input type="number" min="0" step="0.01" value={p.amount}
                placeholder="Сумма"
                onChange={(e) => updatePayment(p.uid, { amount: e.target.value })}
                className="border rounded-lg px-2 py-1.5 text-xs" />
              <input type="datetime-local" value={p.createdAtLocal}
                onChange={(e) => updatePayment(p.uid, { createdAtLocal: e.target.value })}
                className="border rounded-lg px-2 py-1.5 text-xs" />
              <select value={p.pointId} onChange={(e) => updatePayment(p.uid, { pointId: e.target.value })}
                className="border rounded-lg px-2 py-1.5 text-xs col-span-2">
                {points.map((pt) => <option key={pt.id} value={pt.id}>{pt.name}</option>)}
              </select>
              <input value={p.note} onChange={(e) => updatePayment(p.uid, { note: e.target.value })}
                placeholder="Заметка"
                className="border rounded-lg px-2 py-1.5 text-xs col-span-2" />
            </div>
          </div>
        ))}
      </section>

      {/* Note */}
      <section className="bg-white border rounded-2xl p-4 space-y-2">
        <h2 className="font-semibold text-sm">Комментарий</h2>
        <textarea value={manualNote} onChange={(e) => setManualNote(e.target.value)}
          rows={2} placeholder="Источник данных, причина ручного ввода"
          className="w-full border rounded-lg px-2 py-2 text-sm" />
      </section>

      {/* Cascade preview */}
      {totalCascade > 0 && (
        <div className={`rounded-xl px-3 py-2 text-xs ${cascadeOver ? 'bg-red-50 border border-red-200 text-red-700' : 'bg-blue-50 border border-blue-200 text-blue-700'}`}>
          {cascadeOver
            ? `⚠️ Каскад затронет > ${cascadeLimit} смен — лимит превышен. Создать не получится.`
            : `🔄 После сохранения будет пересчитано ${totalCascade} смен (касса на открытие).`
          }
        </div>
      )}

      {/* Errors */}
      {validations.length > 0 && (
        <ul className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2 space-y-0.5">
          {validations.slice(0, 6).map((v) => <li key={v}>• {v}</li>)}
        </ul>
      )}
      {submitError && (
        <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{submitError}</div>
      )}

      {/* Actions */}
      <div className="sticky bottom-0 bg-white border-t -mx-4 px-4 py-3 flex gap-2">
        <button onClick={() => navigate('/owner/rentals')}
          className="flex-1 py-2.5 rounded-lg border border-gray-300 text-sm">
          Отмена
        </button>
        <button onClick={handleSubmit}
          disabled={create.isPending || validations.length > 0 || cascadeOver}
          className="flex-1 py-2.5 rounded-lg bg-brand-600 text-white text-sm font-semibold disabled:opacity-50">
          {create.isPending ? 'Создание…' : 'Создать аренду'}
        </button>
      </div>
    </div>
  )
}
